<template>
	<PageBackground />
	<div class="page-wrapper">
		<Header :planet-path="planetPath" :class="{ animate: animate }" :header="header" />
		<Sidebar :animate="animate" :class="{ animate: animate }" />
	</div>
	<div id="router-view-container">
		<router-view
			:animate="animate"
			:initial-slug="initialSlug"
			:missions="missions"
			:events="events"
			:pilots="livePilots"
			:clocks="liveClocks"
			:reserves="liveReserves"
			:records="records"
			:recaps="recaps"
		/>
	</div>
	<svg
		style="visibility: hidden; position: absolute"
		width="0"
		height="0"
		xmlns="http://www.w3.org/2000/svg"
		version="1.1"
	>
		<defs>
			<filter id="round">
				<feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
				<feColorMatrix
					in="blur"
					mode="matrix"
					values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -5"
					result="goo"
				/>
				<feComposite in="SourceGraphic" in2="goo" operator="atop" />
			</filter>
		</defs>
	</svg>
	<audio autoplay>
		<source src="/startup.ogg" type="audio/ogg" />
	</audio>
</template>

<script>
import Header from "./components/layout/Header.vue";
import Sidebar from "./components/layout/Sidebar.vue";
import PageBackground from "./components/layout/PageBackground.vue";
import Config from "@/data/info/general-config.json";
import { pilots } from "@/data/pilots.js";
import {
	startLiveState,
	withLiveClocks,
	withLivePilot,
	withLiveReserves,
} from "@/data/liveState.js";
import { reserves } from "@/data/reserves.js";

export default {
	components: {
		Header,
		Sidebar,
		PageBackground,
	},

	data() {
		return {
			animate: Config.animate,
			initialSlug: Config.initialSlug,
			planetPath: Config.planetPath,
			header: Config.header,
			pilotSpecialInfo: Config.pilotSpecialInfo,
			clocks: [],
			events: [],
			missions: [],
			pilots: [],
			records: [],
			reserves: [...reserves],
			recaps: [],
			bonds: [],
		};
	},
	computed: {
		// Pilots with what Foundry has: condition, build and active mech (src/data/liveState.js)
		livePilots() {
			return this.pilots.map(withLivePilot);
		},
		// Clocks the GM shows from Foundry, after the files' and the pilots'
		liveClocks() {
			return withLiveClocks(this.clocks);
		},
		// Foundry's company reserves, once it keeps them
		liveReserves() {
			return withLiveReserves(this.reserves);
		},
	},
	created() {
		startLiveState();
		this.setTitleFavicon(Config.defaultTitle + " MISSION BRIEFING", Config.icon);
		this.importMissions(
			import.meta.glob("@/data/missions/*.md", { query: "?raw", import: "default" })
		);
		this.importRecaps(import.meta.glob("@/data/recaps/*.md", { query: "?raw", import: "default" }));
		this.importEvents(import.meta.glob("@/data/events/*.md", { query: "?raw", import: "default" }));
		this.importRecords(
			import.meta.glob("@/data/lore/**/*.md", { query: "?raw", import: "default" })
		);
		// Pilot project clocks are appended to the clock list, so load them after
		this.importClocks(import.meta.glob("@/data/clocks/clocks.json")).then(() => this.loadPilots());
	},
	mounted() {
		// Every visit starts on Status, except the dev-only style gallery
		if (!window.location.pathname.startsWith("/styleguide")) this.$router.push("/status");
	},
	methods: {
		setTitleFavicon(title, favicon) {
			document.title = title;
			let headEl = document.querySelector("head");
			let faviconEl = document.createElement("link");
			faviconEl.setAttribute("rel", "shortcut icon");
			faviconEl.setAttribute("href", favicon);
			headEl.appendChild(faviconEl);
		},
		async importMissions(files) {
			let filePromises = Object.keys(files).map(path => files[path]());
			let fileContents = await Promise.all(filePromises);
			fileContents.forEach(content => {
				let mission = {};
				mission["slug"] = content.split("\n")[0];
				mission["name"] = content.split("\n")[1];
				mission["status"] = content.split("\n")[2];
				mission["content"] = content.split("\n").splice(3).join("\n");
				if (mission["status"] !== "hidden") {
					this.missions = [...this.missions, mission];
				}
			});
			this.missions = this.missions.sort(function (a, b) {
				return b["slug"] - a["slug"];
			});
		},
		async importEvents(files) {
			let filePromises = Object.keys(files).map(path => files[path]());
			let fileContents = await Promise.all(filePromises);
			fileContents.forEach(content => {
				let event = {};
				event["title"] = content.split("\n")[0];
				event["location"] = content.split("\n")[1];
				event["time"] = content.split("\n")[2];
				event["thumbnail"] = content.split("\n")[3];
				event["content"] = content.split("\n").splice(4).join("\n");
				this.events = [...this.events, event];
			});
			this.events = this.events.reverse();
		},
		async importRecords(files) {
			const paths = Object.keys(files);
			const fileContents = await Promise.all(paths.map(p => files[p]()));
			fileContents.forEach((content, i) => {
				const loreRoot = paths[i].indexOf("/data/lore/") + "/data/lore/".length;
				const filename = paths[i].slice(loreRoot);
				let record = {};
				record["filename"] = filename;
				record["title"] = content.split("\n")[0];
				record["location"] = content.split("\n")[1];
				record["time"] = content.split("\n")[2];
				record["thumbnail"] = content.split("\n")[3];
				record["content"] = content.split("\n").splice(4).join("\n");
				this.records = [...this.records, record];
			});
			this.records = this.records.reverse();
		},
		async importRecaps(files) {
			const paths = Object.keys(files);
			const fileContents = await Promise.all(paths.map(p => files[p]()));
			fileContents.forEach((content, i) => {
				const recapsRoot = paths[i].indexOf("/data/recaps/") + "/data/recaps/".length;
				const slug = paths[i].slice(recapsRoot).replace(/\.md$/, "");
				this.recaps = [...this.recaps, { slug, content }];
			});
		},
		async importClocks(files) {
			let filePromises = Object.keys(files).map(path => files[path]());
			let fileContents = await Promise.all(filePromises);
			fileContents.forEach(content => {
				this.clocks = JSON.parse(JSON.stringify(content)).default;
			});
		},
		loadPilots() {
			pilots.forEach(source => {
				const pilot = { ...source, ...this.pilotSpecialInfo[source.callsign.toUpperCase()] };
				this.pilots = [...this.pilots, pilot];
				pilot.clocks.forEach(content => {
					this.clocks = [
						...this.clocks,
						{
							type: `Pilot Project // ${pilot.callsign}`,
							result: "",
							name: content.name,
							description: content.description,
							value: content.progress,
							max: content.segments,
							color: "#3CB043",
						},
					];
				});
				(pilot.bond ? pilot.bond.burdens : [])
					.filter(b => b.segments)
					.forEach(burden => {
						this.clocks = [
							...this.clocks,
							{
								type: `Burden // ${pilot.callsign}`,
								result: "",
								name: burden.name,
								description: `${pilot.bond.name} burden`,
								value: burden.progress,
								max: burden.segments,
								color: "#7A1C2E",
							},
						];
					});
				pilot.reserves.forEach(content => {
					this.reserves = [
						...this.reserves,
						{
							type: content.type,
							name: content.name,
							description: content.description,
							label: content.label,
							cost: content.cost,
							notes: content.notes,
							callsign: pilot.callsign.toUpperCase(),
						},
					];
				});
			});
		},
	},
};
</script>

<style>
#app {
	min-height: 100vh;
	overflow: hidden !important;
	/* border-right: 1px solid #ff0;
	border-bottom: 1px solid #ff0; */
}
</style>
